"use strict";
/**
 * Copyright (c) 2024 BrightSpike
 * All rights reserved.
 *
 * This source code is unlicensed.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 *
 * For detailed license information, please contact Brightspike at (info@brightspike.io).
 * @author Austin Weidler
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CompleteScene = void 0;
const Scene_1 = require("./Scene");
const pixi_js_1 = require("pixi.js");
const SceneManager_1 = require("../managers/SceneManager");
const interfaces_1 = require("../../../shared/interfaces");
const Global_1 = require("../Global");
const gsap_core_1 = require("gsap/gsap-core");
const ui_1 = require("@pixi/ui");
const blocks_1 = require("../blocks");
const config_json_1 = __importDefault(require("../../../shared/config.json"));
const i18next_1 = __importDefault(require("i18next"));
const lodash_1 = __importDefault(require("lodash"));
const medal_bronze_star_svg_1 = __importDefault(require("../../assets/svg/medal_bronze_star.svg"));
const medal_silver_star_svg_1 = __importDefault(require("../../assets/svg/medal_silver_star.svg"));
const medal_gold_star_svg_1 = __importDefault(require("../../assets/svg/medal_gold_star.svg"));
const medal_bronze_svg_1 = __importDefault(require("../../assets/svg/medal_bronze.svg"));
const medal_silver_svg_1 = __importDefault(require("../../assets/svg/medal_silver.svg"));
const medal_gold_svg_1 = __importDefault(require("../../assets/svg/medal_gold.svg"));
const medal_sparkles_svg_1 = __importDefault(require("../../assets/svg/medal_sparkles.svg"));
const brushpoint_svg_1 = __importDefault(require("../../assets/svg/brushpoint.svg"));
const brushbonus_svg_1 = __importDefault(require("../../assets/svg/brushbonus.svg"));
const brushbonus_sparkles_svg_1 = __importDefault(require("../../assets/svg/brushbonus_sparkles.svg"));
const settings_1 = require("../config/settings");
const levels_1 = require("../../../shared/levels");
const LevelsRoomScene_1 = require("./LevelsRoomScene");
const AssetManager_1 = __importDefault(require("../managers/AssetManager"));
class CompleteScene extends Scene_1.Scene {
    constructor(app, sceneManager, gameManager) {
        var _a;
        super(app, sceneManager, gameManager, (0, Global_1.colorForGameType)((_a = gameManager.room$.value) === null || _a === void 0 ? void 0 : _a.type));
        this.medals = [medal_gold_star_svg_1.default, medal_silver_star_svg_1.default, medal_bronze_star_svg_1.default];
        this.medalRanks = [medal_gold_svg_1.default, medal_silver_svg_1.default, medal_bronze_svg_1.default];
        this.bouncedelay = 1.0;
        this.musicVolume = settings_1.Settings.loudMusicVolume;
        this.room = gameManager.room$.value;
        this.confetti = this.addChild(new blocks_1.Confetti(app, app.screen.width, app.screen.height));
        this.confetti.alpha = 0.75;
        this.centerText = this.makeCenterText();
        this.top = this.makeTop();
        this.bottom = this.makeBottom();
        if (this.room.type === interfaces_1.GameType.BattleFriends
            || this.room.type === interfaces_1.GameType.GuessFriends
            || this.room.type === interfaces_1.GameType.Battle
            || this.room.type === interfaces_1.GameType.Guess
            || this.room.type === interfaces_1.GameType.Tutorial) {
            this.content = this.makeTable();
        }
        else if (this.room.type === interfaces_1.GameType.Levels) {
            this.content = this.makeIcon();
            LevelsRoomScene_1.LevelsRoomScene.clearYSpot();
        }
        this.subs.add(gameManager.rank$.subscribe({
            next: (rank) => __awaiter(this, void 0, void 0, function* () {
                if (this.room.type === interfaces_1.GameType.Speed && rank) {
                    this.centerText.removeChildren();
                    this.centerText.text = i18next_1.default.t('speed.myrank', { rank: rank });
                    const medal = this.medalRanks[rank - 1];
                    if (medal) {
                        const med = pixi_js_1.Sprite.from(medal);
                        med.scale.set((0, Global_1.px)(this.isLandscapeBox ? 0.35 : 0.5));
                        med.anchor.set(0.5, 0);
                        med.position.set(0, this.centerText.height);
                        this.centerText.addChild(med);
                    }
                    yield (0, Global_1.fade)(this.centerText, 1, 1, this.bouncedelay);
                }
            })
        }));
    }
    get ranks() {
        var _a;
        const players = lodash_1.default.values(lodash_1.default.cloneDeep((_a = this.room) === null || _a === void 0 ? void 0 : _a.players));
        const sorted = players.sort((p1, p2) => {
            return p2.score - p1.score;
        });
        sorted[0].s = 1;
        for (let i = 1; i < sorted.length; i++) {
            if (sorted[i].score === sorted[i - 1].score) {
                sorted[i].s = sorted[i - 1].s;
            }
            else {
                sorted[i].s = sorted[i - 1].s + 1;
            }
        }
        return sorted;
    }
    makeIcon() {
        var _a;
        const container = new pixi_js_1.Container();
        const mynewlevel = (((_a = this.gameManager.playerManager.data) === null || _a === void 0 ? void 0 : _a.lvl) || 0);
        const text = new pixi_js_1.Text(i18next_1.default.t('levels.unlocked'), blocks_1.Block.textStyle(42));
        text.style.fill = settings_1.Settings.blackColor;
        text.style.align = 'center';
        text.style.lineHeight = (0, Global_1.px)(50);
        text.anchor.set(0.5);
        container.addChild(text);
        if (levels_1.levels[mynewlevel]) {
            this.unlockSprite = pixi_js_1.Sprite.from(require('../../assets/levels/' + levels_1.levels[mynewlevel].badge + '.svg').default);
            this.unlockSprite.scale.set((0, Global_1.px)(0.0001));
            this.unlockSprite.anchor.set(0.5);
            this.unlockSprite.position.y = (0, Global_1.px)(130);
            container.addChild(this.unlockSprite);
        }
        else {
        }
        return this.addChild(container);
    }
    get myrank() {
        const r = this.ranks;
        return r.find((p) => p.pkey === this.gameManager.playerManager.pkey);
    }
    makeBonus() {
        const container = new pixi_js_1.Container();
        const text = new pixi_js_1.Text(i18next_1.default.t('battle.bonus') + ':', blocks_1.Block.textStyle(32));
        const bcontainer = new pixi_js_1.Container();
        for (let i = 0; i < config_json_1.default.winbonus; i++) {
            const bonus = pixi_js_1.Sprite.from(brushbonus_svg_1.default);
            bonus.scale.set((0, Global_1.px)(0.55));
            bonus.anchor.set(0.5);
            bonus.position.set(i * (0, Global_1.px)(35), 0);
            bcontainer.addChild(bonus);
            const bonus_sparkles = pixi_js_1.Sprite.from(brushbonus_sparkles_svg_1.default);
            bonus_sparkles.scale.set((0, Global_1.px)(0.65));
            bonus_sparkles.anchor.set(0.5);
            bonus_sparkles.position.set((i * (0, Global_1.px)(35)) + (0, Global_1.px)(-1), (0, Global_1.px)(-19));
            bcontainer.addChild(bonus_sparkles);
            this.promisedTimeline(gsap_core_1.gsap.timeline()
                .from(bonus, {
                pixi: {
                    alpha: 0,
                    rotation: 60
                },
                duration: 0.5,
                delay: this.bouncedelay + 0.7
            })
                .from(bonus_sparkles, {
                pixi: {
                    alpha: 0
                },
                duration: 0.2
            })
                .to(bonus_sparkles, {
                pixi: {
                    scale: (0, Global_1.px)(0.7)
                },
                repeat: -1,
                duration: 1.1,
                ease: 'steps(1)'
            })).then();
        }
        bcontainer.position.set((0, Global_1.px)(150), (0, Global_1.px)(10));
        container.addChild(text, bcontainer);
        container.pivot.set(container.width / 2, 0);
        return this.addChild(container);
    }
    makeTable() {
        const table = new pixi_js_1.Container();
        const colpad = (0, Global_1.px)(20);
        const rowpad = (0, Global_1.px)(this.overSmallSize ? 54 : 48);
        const makeRow = (data) => {
            const row = new pixi_js_1.Container();
            const medal = this.medals[data.s - 1];
            const mine = data.pkey === this.gameManager.playerManager.pkey;
            const rank = new pixi_js_1.Container();
            // No medals for battling friends :(
            if (medal && (this.room.type === interfaces_1.GameType.Battle || this.room.type === interfaces_1.GameType.Guess)) {
                const med = pixi_js_1.Sprite.from(medal);
                med.scale.set((0, Global_1.px)(0.3));
                med.anchor.set(0, 0.5);
                med.position.set(0, (0, Global_1.px)(10));
                rank.addChild(med);
                // Sparkes
                if (mine) {
                    const sparkle = pixi_js_1.Sprite.from(medal_sparkles_svg_1.default);
                    sparkle.scale.set((0, Global_1.px)(0.32));
                    sparkle.anchor.set(0.5);
                    sparkle.position.set((0, Global_1.px)(11), (0, Global_1.px)(18));
                    sparkle.alpha = 0.85;
                    rank.addChild(sparkle);
                    this.tms.add(gsap_core_1.gsap.timeline({ repeat: -1 }).to(sparkle, {
                        pixi: {
                            scale: (0, Global_1.px)(0.34)
                        },
                        duration: 1.1,
                        ease: 'steps(1)'
                    }));
                }
            }
            const style = blocks_1.Block.textStyle(28, data);
            let txt = data.u;
            if (this.room.type === interfaces_1.GameType.Tutorial) {
                if (data.score) {
                    txt = i18next_1.default.t('tutorial.earned');
                }
                else {
                    txt = '';
                }
            }
            const user = new pixi_js_1.Text(txt, style);
            user.position.x = colpad * 2;
            const score = new pixi_js_1.Container();
            for (let i = 0; i < data.score; i++) {
                const point = pixi_js_1.Sprite.from(brushpoint_svg_1.default);
                point.scale.set((0, Global_1.px)(0.2));
                point.anchor.set(0.5);
                point.position.set(i * (0, Global_1.px)(17), (0, Global_1.px)(10));
                score.addChild(point);
                this.promisedTimeline(gsap_core_1.gsap.timeline().from(point, {
                    pixi: {
                        rotation: 60,
                        alpha: 0
                    },
                    duration: 0.3,
                    delay: this.bouncedelay + (i * 0.1)
                })).then();
            }
            score.position.x = colpad * 12;
            row.addChild(rank, user, score);
            return row;
        };
        const ranks = this.ranks;
        for (let i = 0; i < ranks.length; i++) {
            const row = makeRow(ranks[i]);
            row.position.set(0, i * rowpad);
            table.addChild(row);
        }
        table.pivot.set(table.width / 2, 0);
        return this.addChild(table);
    }
    init() {
        const _super = Object.create(null, {
            init: { get: () => super.init }
        });
        return __awaiter(this, void 0, void 0, function* () {
            this.gameManager.leaveRoom();
            const more = () => __awaiter(this, void 0, void 0, function* () {
                if (!this.isDeinited) {
                    switch (this.room.type) {
                        case interfaces_1.GameType.GuessFriends:
                            yield this.sceneManager.routeTo(SceneManager_1.Scenes.PrivateGuess, this.bottom);
                            break;
                        case interfaces_1.GameType.BattleFriends:
                            yield this.sceneManager.routeTo(SceneManager_1.Scenes.PrivateBattle, this.bottom);
                            break;
                        case interfaces_1.GameType.Battle:
                            yield this.sceneManager.routeTo(SceneManager_1.Scenes.Battle, this.bottom);
                            break;
                        case interfaces_1.GameType.Speed:
                            yield this.sceneManager.routeTo(SceneManager_1.Scenes.Speed, this.bottom);
                            break;
                        case interfaces_1.GameType.Guess:
                            yield this.sceneManager.routeTo(SceneManager_1.Scenes.Guess, this.bottom);
                            break;
                        case interfaces_1.GameType.Levels:
                            yield this.sceneManager.routeTo(SceneManager_1.Scenes.Levels, this.bottom);
                            break;
                        case interfaces_1.GameType.Tutorial:
                        default:
                            yield this.sceneManager.routeTo(SceneManager_1.Scenes.Home, this.bottom || null);
                            break;
                    }
                }
            });
            const other = (this.bottom.getChildAt(0)).timeline(config_json_1.default.tryagaincountdown / 1000);
            this.sigs.push(this.bottom.onPress.connect(more));
            this.promisedTimeline(other).then(more);
            yield _super.init.call(this);
            if (this.unlockSprite) {
                const endscale = 0.425;
                this.unlockSprite.rotation = -Math.PI;
                yield this.promisedTimeline(gsap_core_1.gsap.timeline({ paused: false })
                    .to(this.unlockSprite, {
                    pixi: {
                        scale: (0, Global_1.px)(endscale * 1.3),
                        rotation: Math.PI
                    },
                    duration: 0.58,
                    ease: 'power2.out'
                }).to(this.unlockSprite, {
                    pixi: {
                        scale: (0, Global_1.px)(endscale),
                        rotation: 0
                    },
                    duration: 0.28,
                    ease: 'power2.in'
                }));
            }
        });
    }
    enterScene(from, target) {
        const _super = Object.create(null, {
            enterScene: { get: () => super.enterScene }
        });
        return __awaiter(this, void 0, void 0, function* () {
            // Slide in
            AssetManager_1.default.wooshSound();
            this.position.y = -this.app.screen.height;
            yield this.promisedTimeline(gsap_core_1.gsap.timeline()
                .to(this, {
                pixi: {
                    positionY: 0
                },
                ease: 'bounce.out',
                duration: this.bouncedelay
            }));
            return _super.enterScene.call(this, from);
        });
    }
    exitScene(to, target) {
        const _super = Object.create(null, {
            exitScene: { get: () => super.exitScene }
        });
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            // Find our button
            this.zIndex = SceneManager_1.Layer.Top;
            const mybutton = to.getChildByName('game' + (((_a = this.room) === null || _a === void 0 ? void 0 : _a.type) || 0), true);
            if (mybutton instanceof ui_1.ButtonContainer) {
                yield this.animateMask(mybutton, true);
            }
            else if (((_b = this.room) === null || _b === void 0 ? void 0 : _b.type) === interfaces_1.GameType.Tutorial || ((_c = this.room) === null || _c === void 0 ? void 0 : _c.type) === interfaces_1.GameType.Levels) {
                yield this.animateFall();
            }
            return _super.exitScene.call(this, to);
        });
    }
    makeCenterText() {
        const ranker = new pixi_js_1.Text(null, blocks_1.Block.textStyle());
        ranker.anchor.set(0.5);
        ranker.style.align = 'center';
        ranker.visible = false;
        return this.addChild(ranker);
    }
    makeBottom() {
        var _a;
        const wait = new blocks_1.FillingButton(this.app, 150, 50, settings_1.Settings.whiteColor, (0, Global_1.colorForGameType)((_a = this.room) === null || _a === void 0 ? void 0 : _a.type), false);
        switch (this.room.type) {
            case interfaces_1.GameType.Battle:
            case interfaces_1.GameType.BattleFriends:
                wait.setText(i18next_1.default.t('battle.again'));
                break;
            case interfaces_1.GameType.Speed:
                wait.setText(i18next_1.default.t('speed.again'));
                break;
            case interfaces_1.GameType.Guess:
            case interfaces_1.GameType.GuessFriends:
                wait.setText(i18next_1.default.t('guesser.again'));
                break;
            case interfaces_1.GameType.Tutorial:
                wait.setText(i18next_1.default.t('tutorial.again'));
                break;
            case interfaces_1.GameType.Levels:
                wait.setText(i18next_1.default.t('levels.again'));
                break;
            default:
                wait.setText(i18next_1.default.t('again'));
        }
        const button = wait.asButton(0.01);
        button.scale.set(this.overSmallSize ? 1 : 0.8);
        return this.addChild(button);
    }
    makeTop() {
        const fontsize = 32;
        const style = blocks_1.Block.textStyle(fontsize);
        style.align = 'center';
        style.lineHeight = (0, Global_1.px)(fontsize * 1.5);
        style.wordWrap = true;
        style.wordWrapWidth = this.app.screen.width;
        let t;
        switch (this.room.type) {
            case interfaces_1.GameType.BattleFriends:
            case interfaces_1.GameType.GuessFriends:
            case interfaces_1.GameType.Guess:
            case interfaces_1.GameType.Battle:
            case interfaces_1.GameType.Tutorial:
                const r = this.myrank;
                t = i18next_1.default.t(((r === null || r === void 0 ? void 0 : r.s) === 1 ? 'congrats' : 'nice'));
                if (this.room.type === interfaces_1.GameType.Battle || this.room.type === interfaces_1.GameType.Guess) {
                    t += '\n' + i18next_1.default.t('battle.place.' + ((r === null || r === void 0 ? void 0 : r.s) || ''));
                }
                else if (this.room.type === interfaces_1.GameType.Tutorial) {
                    t += '\n' + i18next_1.default.t('tutorial.complete');
                }
                break;
            case interfaces_1.GameType.Speed:
                t = i18next_1.default.t('speed.done', { rounds: config_json_1.default.speedrounds, time: (0, Global_1.timed)(this.room.time) });
                break;
            default:
                break;
        }
        const text = new pixi_js_1.Text(t, style);
        text.anchor.set(0.5, 0);
        return this.addChild(text);
    }
    backTo() {
        return (0, Global_1.defaultBackToForType)(this.room.type);
    }
    get triggersStop() {
        return true;
    }
    layout() {
        var _a, _b;
        super.layout();
        const h = this.app.screen.height;
        this.top.position.set(this.center.x, this.isLandscapeBox ? (0, Global_1.px)(8) : h * 0.15);
        this.bottom.position.set(this.center.x, h - this.bottom.height - (0, Global_1.px)(this.overSmallSize ? 50 : 30));
        this.centerText.position.set(this.center.x, this.center.y);
        (_a = this.content) === null || _a === void 0 ? void 0 : _a.position.set(this.center.x, h * 0.5 - (0, Global_1.px)(50));
        (_b = this.bonus) === null || _b === void 0 ? void 0 : _b.position.set(this.center.x, h * 2 / 3 + (0, Global_1.px)(40));
    }
}
exports.CompleteScene = CompleteScene;
